import type {
  common as commonEn,
  projectHome as projectHomeEn,
  createProject as createProjectEn,
  settings as settingsEn,
  projects as projectsEn
} from "./en";

type LocaleShape<T> = {
  [K in keyof T]: T[K] extends string ? string : LocaleShape<T[K]>;
};

export const common: LocaleShape<typeof commonEn> = {
  loadingProject: "プロジェクトを読み込んでいます...",
  loadingChapter: "読み込んでいます...",
  loadingMessages: "メッセージを読み込んでいます...",
  export: "エクスポート",
  exportAs: "エクスポート",
  exporting: "エクスポート中...",
  fileName: "ファイル名",
  cancel: "キャンセル",
  submit: "送信",
  addFiles: "ファイルを追加",
  clearChat: "チャットをクリア",
  clear: "クリア",
  close: "閉じる",
  confirm: "確認",
  loading: "読み込み中",
  optional: "任意",
  notifications: {
    title: "通知",
    markAllAsRead: "すべて既読にする",
    loadFailed: "通知を読み込めませんでした",
    chatResponded: "Leo が返信しました: {{project}}",
    chatRespondedGeneric: "Leo が返信しました",
    analysisReady: "分析の準備ができました: {{project}}",
    analysisReadyGeneric: "分析の準備ができました",
    analysisReadySubtitle: "プロジェクトの分析をご覧いただけます。",
    triggerUnread: "通知、未読 {{count}} 件",
    markRead: "既読にする",
    markGroupRead: "{{name}} を既読にする",
    groupCount_one: "{{count}} 件の通知",
    groupCount_other: "{{count}} 件の通知",
    groupUnread: "未読 {{count}} 件",
    unread: "未読",
    loadingList: "通知を読み込んでいます",
    loadFailedHint: "接続を確認して、もう一度お試しください。",
    retry: "再試行",
    caughtUpTitle: "新しい通知はありません",
    caughtUpDescription: "完了した分析と Leo の返信がここに表示されます。"
  },
  timeAgo: {
    justNow: "たった今",
    minutes: "{{count}} 分前",
    hours: "{{count}} 時間前",
    days: "{{count}} 日前",
    weeks: "{{count}} 週間前",
    months: "{{count}} か月前"
  },
  status: {
    pass: "合格",
    conditional: "条件付き",
    fail: "不合格",
    unvalidated: "未検証",
    not_defined: "未定義"
  },
  versions: {
    newTitle: "新規バージョン",
    renameTitle: "バージョンの名前を変更",
    deleteTitle: "バージョンを削除",
    nameLabel: "バージョン名",
    namePlaceholder: "新規バージョン...",
    baseOn: "ベースにするバージョン",
    scope: "範囲",
    frameOnly: "フレームのみ",
    frameOnlyDescription: "プロジェクトセットアップとケースフレーミングのみで、分析は含みません。",
    fullVersion: "フルバージョン",
    fullVersionDescription: "分析と結果を含む、すべてを含みます。",
    create: "バージョンを作成",
    save: "保存",
    confirm: "確定",
    close: "閉じる",
    untitledProject: "プロジェクト",
    actions: "バージョンの操作",
    rename: "名前を変更",
    delete: "削除",
    deleteQuestion: "{{project}} から {{version}} を削除しますか？",
    deleteQuestionNoProject: "{{version}} を削除しますか？",
    deleteBody: "バージョンのデータと設定はすべて完全に削除されます。",
    deletedToast: "バージョン {{name}} を削除しました。",
    createdToast: "バージョン {{name}} を作成しました。",
    renamedToast: "バージョンの名前を {{name}} に変更しました。",
    removedToast: "{{name}} を削除しました。",
    restoredToast: "{{name}} を復元しました。",
    undo: "元に戻す",
    view: "表示",
    errors: {
      noProject: "複製するプロジェクトを特定できませんでした",
      nameRequired: "名前が必要です",
      renameFailed: "名前を変更できませんでした",
      restoreFailed: "バージョンを復元できませんでした",
      deleteFailed: "バージョンを削除できませんでした",
      createFailed: "バージョンを作成できませんでした",
      duplicateFailed: "複製できませんでした"
    }
  },
  versionMenu: {
    versions: "バージョン",
    latest: "最新",
    draft: "下書き",
    trigger: "バージョン: {{name}}。バージョンを変更",
    rowActions: "{{name}} の操作",
    rename: "名前を変更",
    remove: "削除",
    keepOneVersion: "プロジェクトには少なくとも 1 つのバージョンが必要です。",
    newVersion: "新規バージョン",
    createVersion: "バージョンを作成",
    creating: "作成中…",
    firstVersionDescription:
      "プロジェクトはそのままにして、コピーで作業します。どちらもプロジェクトに残り、切り替えることができます。",
    newVersionDescription: "元のバージョンに影響を与えずに変更できるコピーです。",
    name: "名前",
    nameTaken: "同じ名前のバージョンがすでにあります。",
    basedOn: "ベースにするバージョン",
    draftNoAnalysis: "下書き、分析はまだありません",
    copy: "コピーする内容",
    frameOnly: "フレームのみ",
    frameOnlyDescription:
      "プロジェクトセットアップとケースフレームをコピーします。内容を確認してから、分析を再度開始してください。",
    fullAnalysis: "分析全体",
    fullAnalysisDescription: "分析とその結果を含め、すべてをコピーします。",
    noAnalysisYet: "分析済みのバージョンはまだありません。",
    renameTitle: "バージョンの名前を変更",
    renameDescription: "変更されるのは名前だけです。バージョンの内容はそのまま残ります。",
    save: "保存",
    saving: "保存中…"
  },
  labsMode: {
    title: "実験モード",
    description: "実験的機能と早期アクセス機能を有効にします",
    menuDescription: "現在テスト中の実験的機能をご利用いただけます",
    enableFailed: "実験モードを有効にできませんでした",
    disableFailed: "実験モードを無効にできませんでした"
  },
  userMenu: {
    profile: "プロフィール",
    organization: "組織",
    privacyNotice: "プライバシー通知",
    termsOfUse: "利用規約",
    cookieNotice: "Cookie通知",
    signOut: "サインアウト",
    signOutError: "サインアウトに失敗しました。もう一度お試しください。",
    menuLabel: "{{name}} メニュー",
    organizationSettings: "組織の設定",
    organizationSettingsDescription: "名前、ロゴ、AI モデル、指示",
    teamMembers: "チームメンバー",
    teamMembersDescription: "メンバーの招待とロールの管理",
    account: "アカウント",
    yourProfile: "プロフィール",
    legal: "法的情報"
  }
};

export const projectHome: LocaleShape<typeof projectHomeEn> = {
  sidebar: {
    home: "ホーム",
    projectHome: "プロジェクトホーム",
    analysis: "分析",
    foundation: "基盤",
    executiveSummary: "エグゼクティブサマリー",
    innoveraMemo: "Innovera メモ",
    deepDives: "ディープダイブ",
    marketAnalysis: "市場分析",
    strategyAnalysis: "戦略分析",
    financialAnalysis: "財務分析",
    appendices: "付録",
    hiddenChapters: "非表示のページ",
    context: "コンテキスト",
    frame: "フレーム",
    projectSetup: "プロジェクト設定",
    caseFrame: "ケースフレーム",
    valueChain: "バリューチェーン",
    vault: "保管庫",
    artifacts: "成果物",
    presentations: "プレゼンテーション",
    exports: "エクスポート",
    modelLedger: "モデル台帳",
    rqaSim: "Innovera RQASim",
    rqa: "Innovera RQA",
    collapse: "折りたたむ",
    expand: "展開",
    collapseSidebar: "サイドバーを折りたたむ",
    expandSidebar: "サイドバーを展開",
    locked: "現在はロックされています",
    lockedIcon: "ロック中",
    navLabel: "プロジェクト"
  },
  rqaSim: {
    empty: "この実行では、ページが依拠できる実用的なモデルが生成されませんでした。分析を再実行してください。",
    emptyNoLedger: "このプロジェクトにはまだモデル台帳がないため、動かせるものがありません。",
    ownSettings: "これはご自身の設定です。分析が公表した数値ではありません。戻すにはリセットしてください。",
    showing: "このページが示しているもの",
    quickSets: "クイックセット",
    inView: "{{total}} 件中 {{shown}} 件を表示中。",
    whereItLands: "行き着く先",
    reset: "分析の値に戻す",
    otherInputs: "その他の入力",
    published: "分析の公表値は {{value}}",
    held: "公表値のままです。この設定では数式が解けません。",
    noReturn: "この設定では利率がありません。ケースはゼロに戻りません。",
    notInView: "この入力が価格付けする対象は表示されていません。",
    outsideBand: "分析が裏付ける範囲（{{low}} 〜 {{high}}）の外です。",
    unresolvedOne: "{{count}} 件の数値はこの設定で再計算できず、分析の値のままです: {{keys}}",
    unresolvedMany: "{{count}} 件の数値はこの設定で再計算できず、分析の値のままです: {{keys}}",
    custom: "カスタム",
    customNote: "シナリオなし — これはあなた自身の設定です。",
    noReturnOne:
      "{{count}} 件の数値はこの設定では利率がなく（ケースがゼロに戻らないため）、ダッシュで表示しています: {{keys}}",
    noReturnMany:
      "{{count}} 件の数値はこの設定では利率がなく（ケースがゼロに戻らないため）、ダッシュで表示しています: {{keys}}",
    pinnedBy: "{{name}}がピン留め",
    pinFull: "1つのプロジェクトにピン留めできる指標は最大{{count}}件です。",
    configure: "設定",
    configureClose: "設定を閉じる",
    open: "Innovera RQASimを開く",
    exportReport: "PDFでレポートを書き出す",
    advanced: "詳細設定",
    variableCount: "変数{{count}}件",
    searchVariables: "変数を検索",
    wasValue: "元は{{value}}",
    done: "完了",
    addWidget: "ウィジェットを追加",
    describeWidget: "ウィジェットを説明",
    describeHint: "見たいものを書いてください。現在の設定の変数から作成されます。",
    describePlaceholder: "例: オプションごとのMW当たり資本をバーで",
    describing: "ウィジェットを作成中…",
    pinInstead: "または指標をピン留め",
    filters: "フィルター",
    filterGroups: "グループ",
    changedOnly: "変更のみ",
    resetShort: "リセット",
    noVariables: "一致する変数がありません。",
    unpin: "このウィジェットを外す",
    removedTile: "{{name}}を外しました",
    undo: "元に戻す",
    body: "そこから導かれること"
  },
  risks: {
    title: "Innovera RQA",
    tabOverview: "概要",
    tabTable: "リスク",
    columnRisk: "リスク",
    columnImpact: "影響度",
    columnUncertainty: "不確実性",
    columnStatus: "ステータス",
    empty: "このプロジェクトにはまだリスクがありません。追加するか、分析と一緒に生成されます。",
    addTitle: "リスクを追加",
    editTitle: "リスクを編集",
    addressTitle: "リスクに対処",
    addressRisk: "リスクに対処",
    addressed: "対処済み",
    fieldTitle: "タイトル",
    fieldTitlePlaceholder: "リスクに名前を付ける",
    fieldWhatMustHoldTrue: "成立すべき前提",
    fieldWhyItMatters: "なぜ重要か",
    fieldImpact: "影響度",
    fieldUncertainty: "不確実性",
    summaryTitle: "リスクの現状",
    summaryTotal: "リスク",
    summaryOpen: "未対応",
    summaryCruxOpen: "核心の未対応リスク",
    summaryAddressed: "対応済み",
    graphTitle: "影響度と不確実性",
    nextTitle: "次に対応するリスク",
    allAddressed: "すべてのリスクに対応済みです。",
    axisImpact: "評決への影響度",
    axisUncertainty: "不確実性：どれだけ未確定か",
    quadrant: {
      pinDown: "要確定",
      crux: "核心",
      noted: "記録",
      monitor: "監視"
    },
    level: {
      low: "低",
      medium: "中",
      high: "高"
    },
    plan: "計画",
    planPlaceholder: "このリスクにどう対処しますか？",
    results: "結果",
    resultsPlaceholder: "対処した結果どうなったか",
    attachFile: "ファイルを添付",
    submit: "送信",
    save: "保存",
    edit: "編集",
    cancel: "キャンセル",
    created: "リスクを追加しました",
    updated: "リスクを更新しました",
    saveFailed: "問題が発生しました。もう一度お試しください。",
    fileOpenFailed: "ファイルを開けませんでした。もう一度お試しください。"
  },
  leo: {
    title: "Leo",
    placeholderGenerating: "分析の完了後にチャットをご利用いただけます…",
    updatedChapters: "更新したページ: {{chapters}}",
    stillRunning:
      "Leo は前のリクエストをまだ処理中のため、メッセージは送信されませんでした。メッセージは入力欄に戻してあります。Leo の処理が終わってから送信してください。",
    send: "送信",
    clear: "クリア",
    clearTitle: "このチャットをクリアしますか？",
    cancel: "キャンセル",
    chatCleared: "チャットをクリアしました",
    placeholderAnything: "Leo に何でも質問してください",
    welcomeTitle: "今日は何をお手伝いしましょうか？",
    welcomeSubtitle: "このプロジェクト、ソース、レポートについて質問してください。",
    suggestionStands: "このプロジェクトの現状をまとめて",
    suggestionUnproven: "まだ立証されていないことは？",
    suggestionNext: "次に何をすべき？",
    disclaimer: "Leo は間違えることがあります。重要な結果は確認してください。",
    hide: "Leo を非表示",
    ask: "Leo に質問",
    updatingAnalysis: "分析を更新しています。更新の準備ができるまで、現在の内容はそのまま画面に表示されます。",
    status: {
      starting: "Leo が準備しています…",
      reading: "{{chapter}} を読んでいます…",
      editing: "{{chapter}} を編集しています…",
      searching: "メモ内で「{{query}}」を検索しています…",
      thinking: "考えています…",
      replying: "返信を書いています…"
    }
  },
  modelLedger: {
    title: "モデル台帳",
    searchPlaceholder: "変数を検索",
    clearSearch: "検索をクリア",
    filter: "フィルター",
    reset: "リセット",
    filterRole: "役割",
    filterSource: "出典",
    more: "その他の操作",
    downloadModel: "model.py をダウンロード",
    figureCardDependsOn: "依存する数値",
    columnVariable: "変数",
    columnValue: "値",
    columnSource: "出典",
    columnRole: "役割",
    columnWhatThisIs: "説明",
    sourceCalculated: "計算値",
    sourceBrief: "ケースブリーフ",
    sourceDocument: "保管庫",
    originDocument: "保管庫の文書",
    downloading: "ダウンロード中…",
    sourceWeb: "ウェブ",
    sourced: "出典",
    sourceAssumption: "仮定",
    sourceOther: "入力値",
    roleObjective: "目的",
    roleDecision: "意思決定",
    roleConstraint: "制約",
    roleAssumption: "仮定",
    roleParameter: "パラメータ",
    roleOutput: "アウトプット",
    roleIntermediate: "中間値",
    whatThisIs: "この数値について",
    factBinding: "拘束",
    factProvenance: "出所",
    factConfidence: "確度",
    factTreatment: "扱い",
    factAsOf: "時点",
    factAssumedFrom: "前提とした数値",
    factRelatedTo: "連動する数値",
    provenanceFrame: "フレームで設定",
    provenanceClient: "クライアント提供",
    provenanceResearch: "調査による",
    provenanceBenchmark: "類似事例から",
    provenanceExpert: "専門家から",
    provenanceFeed: "ライブフィードから",
    provenanceComputed: "ここで計算",
    provenanceEngine: "エンジンによる仮定",
    confidenceValidated: "検証済み",
    confidenceEstimated: "推定",
    bindingHard: "必須",
    bindingSoft: "目標値",
    treatmentHeld: "固定",
    treatmentSwept: "変動させた",
    treatmentSolved: "求解",
    treatmentTracked: "追跡中",
    unverified: "未検証",
    unverifiedWhy:
      "このランの model.py は {{printed}} を出力しましたが、プラットフォームの計算では {{computed}} となり、ページにはこちらを表示しています。両者はこの数式を異なる形で解釈しており、エンジニアに通知済みです。",
    movedBy: "{{date}} に {{by}} がチャットで変更: {{from}} → {{to}}",
    movedEarlier: "以前チャットで変更: 変更前 {{from}}",
    moved: "{{date}} にチャットで変更: {{from}} → {{to}}",
    whereFrom: "この値の出所",
    drives: "この値が影響する数値",
    checksTitle: "モデルの整合性チェック",
    checksSummary: "{{total}} 件中 {{holding}} 件が成立",
    checkHolds: "成立",
    checkFails: "不成立",
    checkNotEvaluated: "検証できません",
    valueNotComputed: "計算できません",
    whyNotComputed: "計算できない理由",
    empty: "このプロジェクトにはまだモデル台帳がありません。台帳はプロジェクト生成時に分析と一緒に作成されます。",
    noMatches: "該当する変数はありません。"
  },
  valueChain: {
    readOnly: "読み取り専用",
    title: "バリューチェーン",
    generate: "生成",
    regenerate: "再生成",
    generating: "生成中...",
    generatedToast: "バリューチェーンを生成しました",
    regeneratedToast: "バリューチェーンを再生成しました",
    generationFailed: "生成に失敗しました。もう一度お試しください。",
    loadErrorTitle: "バリューチェーンを読み込めませんでした",
    loadErrorBody: "取得中に問題が発生しました。接続を確認して、もう一度お試しください。",
    retry: "再試行",
    emptyTitle: "バリューチェーンはまだありません",
    emptyBodyEditor: "このプロジェクトの分析、保管庫のドキュメント、概要からバリューチェーンを生成します。",
    emptyBodyViewer:
      "プロジェクトの編集者が、このプロジェクトの分析、保管庫のドキュメント、概要からバリューチェーンを生成できます。",
    footerNote:
      "プロジェクトの分析と保管庫から生成されています。注釈は上記の分析を引用しています。最新の内容を反映するには再生成してください。",
    upstream: "川上",
    downstream: "川下",
    scrollEarlier: "前の段階へスクロール",
    scrollLater: "次の段階へスクロール",
    aboutPosition: "{{label}}について",
    whoHoldsTitle: "各段階を誰が担い、何が特徴となっているか？",
    colStage: "段階",
    colWhoHolds: "現在の担い手",
    colCharacterizes: "特徴",
    annotation: {
      concentration: "集中度",
      rivalry: "競争",
      inputDifficulty: "調達難易度",
      valueCapture: "価値獲得"
    },
    position: {
      base: "ベースケース",
      candidate: "選択肢",
      recommended: "推奨",
      rejected: "却下"
    },
    owner: {
      ours: "現在の自社",
      suppliers: "サプライヤー",
      customers: "顧客",
      others: "第三者",
      nobody: "未確定"
    },
    errors: {
      notEnabled: "この組織ではバリューチェーンが有効になっていません",
      inProgress: "バリューチェーンの生成はすでに進行中です",
      noContent:
        "このプロジェクトにはまだ分析やケースブリーフがありません — 先にプロジェクトのコンテンツを生成してください",
      generationFailed: "生成に失敗しました。もう一度お試しください。"
    }
  },
  chapterBlocks: {
    theNumbers: "数値",
    rowsNotCharted_one: "{{count}} 行はグラフに表示されていません。下の表に含まれています。",
    rowsNotCharted_other: "{{count}} 行はグラフに表示されていません。下の表に含まれています。",
    figuresErrorTitle: "このページの数値を読み込めませんでした",
    figuresErrorBody:
      "このページの数値はモデル台帳から書き込まれますが、台帳を読み込めませんでした。接続を確認して、もう一度お試しください。",
    empty: "このページにはまだ内容がありません。",
    retry: "再試行"
  },
  chapterHeader: {
    aq: "AQ",
    aqTitle: "分析クエスチョン",
    iq: "IQ",
    iqTitle: "インテリジェンスクエスチョン",
    claims: "クレーム",
    claimsTitle: "クレーム抽出",
    comments: "コメント",
    commentsTitle: "コメント",
    vault: "保管庫",
    vaultTitle: "ドキュメント保管庫",
    fullWidth: "全幅表示",
    defaultWidth: "標準幅表示",
    moreOptions: "その他のオプション",
    markdown: "Markdown",
    copy: "コピー",
    paste: "貼り付け",
    noContentToCopy: "コピーする内容がありません",
    figuresNotReady: "チャプターの数値がまだ読み込まれていません。少し待ってからもう一度お試しください。",
    markdownCopied: "Markdown をクリップボードにコピーしました",
    copyFailed: "Markdown のコピーに失敗しました",
    noContentInClipboard: "クリップボードに内容がありません",
    editorNotReady: "エディターの準備ができていません",
    markdownPasted: "Markdown を貼り付けました",
    pasteFailed: "Markdown の貼り付けに失敗しました"
  },
  comments: {
    title: "コメント",
    close: "コメントを閉じる",
    loading: "コメントを読み込んでいます…",
    emptyTitle: "コメントはまだありません",
    emptyDescription: "チャプターのテキストを選択してコメントするか、下にメモを残してください。",
    showInChapter: "チャプター内で表示",
    resolve: "解決",
    actions: "コメントの操作",
    edit: "編集",
    delete: "削除",
    cancel: "キャンセル",
    addComment: "コメントを追加",
    postComment: "コメントを投稿",
    reply: "返信",
    sendReply: "返信を送信",
    editComment: "コメントを編集",
    save: "保存",
    replyCount_one: "{{count}} 件の返信",
    replyCount_other: "{{count}} 件の返信"
  },
  header: {
    document: "ドキュメント",
    presentation: "プレゼンテーション",
    presentationDisabledTooltip: "ドキュメントの生成が完了すると、プレゼンテーションが利用可能になります。",
    exportProject: "プロジェクトをエクスポート",
    exportPresentation: "プレゼンテーションをエクスポート",
    duplicateProject: "プロジェクトを複製",
    duplicateConfirm: "このプロジェクトを複製してもよろしいですか？",
    duplicateAction: "複製",
    duplicateFailed: "プロジェクトの複製に失敗しました",
    duplicateSuccess: "プロジェクトを複製しました",
    switchedTo: "{{name}} に切り替えました。",
    exportDocSuccess: "ドキュメントをエクスポートしました",
    exportDocFailed: "ドキュメントのエクスポートに失敗しました",
    exportDownloadFailed: "ファイルのダウンロードに失敗しました",
    exportFormatUnavailable: "このプレゼンテーションでは {{format}} は利用できません",
    brandLabel: "Innovera ホーム",
    breadcrumb: "パンくずリスト"
  },
  overview: {
    opportunityFrame: "機会フレーム",
    successCriteria: "成功基準",
    keyInsights: "主要なインサイト",
    nextSteps: "次のステップ",
    target: "目標: {{target}}",
    outOfDate: "最新ではありません",
    outOfDateTooltip: "最新ではありません - 更新してください",
    refresh: "更新",
    refreshing: "更新中…",
    updated: "{{date}} に更新",
    failedToRefresh: "更新に失敗しました",
    generating: "概要を生成中",
    legendCount: "{{value}} {{status}}",
    whereItStands: "現在の状況",
    question: "問い",
    verdict: "判定",
    recommendation: "推奨",
    recommendationPending: "推奨は分析の実行後に表示されます。",
    criteriaMet: "{{total}} 件中 {{score}} 件の基準を達成",
    sourceCount_one: "ソース <count>{{count}}</count> 件",
    sourceCount_other: "ソース <count>{{count}}</count> 件",
    chaptersReady: "<total>{{total}}</total> 章中 <ready>{{ready}}</ready> 章が完成"
  },
  chat: {
    title: "チャット",
    hide: "チャットを非表示",
    askAnything: "何でも質問してください...",
    chatDisabled: "チャットは無効です",
    clearChatConfirm: "チャットをクリアしてもよろしいですか？",
    clearThisChat: "このチャットをクリアしますか？",
    chatCleared: "チャットをクリアしました"
  },
  caseFrame: {
    relaunch: "分析を再実行",
    relaunching: "再実行中...",
    relaunchDescription:
      "変更を適用した新しいバージョンが作成されます。自動的にリダイレクトされ、分析が再開されます。10〜30分ほどかかる場合があります。",
    relaunchConfirm: "作成して再実行",
    empty: "このプロジェクトにはまだケースフレームがありません。",
    relaunchFailed: "分析の再実行に失敗しました",
    saveFailed: "変更の保存に失敗しました。もう一度お試しください。",
    instructionsSaveFailed: "指示の保存に失敗しました。もう一度お試しください。"
  },
  projectSetup: {
    visibility: "公開範囲",
    organization: "組織",
    private: "非公開",
    language: "言語",
    version: "バージョン",
    versionInitial: "初版",
    vault: "保管庫",
    attachment: "添付ファイル",
    attachments: "添付ファイル",
    instructions: "指示",
    namePlaceholder: "プロジェクト名",
    descriptionPlaceholder: "このプロジェクトの簡単な説明を入力してください",
    dimensionAdd: "追加",
    dimensionAddValue: "値を入力",
    dimensionSave: "保存",
    photoUpload: "プロジェクト写真をアップロード",
    photoExpand: "プロジェクト写真を拡大",
    photoInvalidType: "JPG、PNG、WebP形式の画像のみアップロードできます。",
    photoTooLarge: "画像サイズは5MB以下にしてください。",
    photoUploadFailed: "写真をアップロードできませんでした。もう一度お試しください。",
    languageName: {
      english: "英語",
      japanese: "日本語"
    }
  },
  dashboard: {
    title: "ダッシュボード",
    chapter: "チャプター",
    taskInProgress: "進行中のタスク",
    complete: "完了",
    view: "表示",
    documentsBeingAnalyzed: "ドキュメントを分析しています",
    analyzeTimeEstimate: "通常1〜3分かかります"
  },
  generation: {
    backToProjectList: "プロジェクト一覧に戻る",
    analysisInProgressTitle: "分析を実行中です",
    analysisInProgressDescription:
      "ドキュメントを確認し、詳細な分析を作成しています。このページを離れても問題ありません。完了したらお知らせします。",
    failedTitleRateLimit: "分析が利用上限に達しました",
    failedDescriptionRateLimit:
      "現在処理能力に限りがあるため、分析を完了できませんでした。再試行すると中断したところから続行され、すでに作成されたページは保持されます。",
    failedTitleTimeout: "分析が制限時間を超えました",
    failedDescriptionTimeout:
      "この処理は制限時間よりも長くかかりました。再試行すると、すでに完了したページから再開します。",
    failedTitleGeneric: "分析を完了できませんでした",
    failedDescriptionGeneric:
      "分析の作成中に問題が発生し、チームに通知されました。再試行するか、これまでに完了した内容を表示できます。",
    failedRetry: "分析を再試行",
    readyIn: "完了まで",
    readyInHours: "約{{hours}}時間",
    readyInMinutes: "約{{minutes}}分",
    readyInHoursMinutes: "約{{hours}}時間{{minutes}}分",
    readySoonLabel: "完了まで",
    readySoon: "まもなく",
    setupStarted:
      "分析を開始しました。ドキュメントを確認し、詳細な分析を作成しています。このページを離れても問題ありません。完了したらお知らせします。",
    workingOnNow: "現在の処理:",
    setupPhase: {
      orienting: "ドキュメントとブリーフを確認しています",
      modeling: "基礎モデルを構築しています",
      ledger: "数値を計算しています",
      chapters: "分析を作成しています",
      twin: "インタラクティブモデルを組み立てています",
      storing: "分析を仕上げています"
    },
    queuedTitle: "{{title}}は生成待ちです",
    queuedFallbackTitle: "このページ",
    queuedStartsAfter: "次のページが完了すると開始されます:",
    queuedStartsAfterOne: "次のページが完了すると開始されます:",
    queuedStartsShortly: "まもなく開始されます。",
    generatingChapter: "ページを生成中",
    progressStarted:
      "分析を開始しました。完了まで10〜30分かかります。このページを離れても分析は続行され、後で戻って確認できます。",
    progressOrder: "ページは依存関係の順に作成されるため、完了する順番は前後します。",
    openChapter: "{{name}}を開く",
    cancelAnalysis: "分析をキャンセル",
    cancelAnalysisDescription:
      "分析を停止し、プロジェクトをケースフレームの段階に戻します。これまでの進捗は失われます。",
    keepGenerating: "生成を続ける",
    close: "閉じる"
  },
  exports: {
    empty: "まだエクスポートはありません",
    open: "開く",
    download: "ダウンロード",
    downloadFailed: "ファイルのダウンロードに失敗しました",
    deck: "スライド",
    memo: "メモ",
    emptyDescription: "メモやスライドをエクスポートして共有できます。エクスポートはすべてここに保存されます。",
    exportedOnBy: "{{date}}に{{author}}がエクスポート",
    exportedOn: "{{date}}にエクスポート",
    exportedBy: "{{author}}がエクスポート",
    rowActions: "{{name}} の操作"
  },
  exportOptions: {
    close: "閉じる",
    type: "種類",
    fullReport: "完全版レポート",
    fullReportPages: "すべてのページを完全に収録します。",
    fullReportChapters: "すべてのチャプターを完全に収録します。",
    summary: "サマリー",
    summaryDescription: "要点をまとめた概要のみ。",
    include: "含める内容",
    projectFrame: "プロジェクトフレーム",
    projectInstructions: "プロジェクト指示",
    analysisQuestions: "分析クエスチョンとインテリジェンスクエスチョン",
    vaultFilesAndLinks: "保管庫のファイルとリンク"
  },
  vaultPage: {
    downloadAll: "すべてダウンロード",
    downloading: "ダウンロード中…",
    downloadAllFailed: "ファイルを一括ダウンロードできませんでした",
    empty: "保管庫にはまだファイルがありません",
    emptyDescription: "このプロジェクトで参照するドキュメント、データ、議事録をアップロードしてください。",
    uploading: "アップロード中: {{files}}",
    uploadingMeta: "アップロード中…",
    addedOn: "{{date}}に追加",
    addedBy: "追加者: {{name}}",
    rowActions: "{{name}} の操作",
    processing: "処理中…",
    processFailed: "処理に失敗しました",
    openLink: "リンクを開く",
    download: "ダウンロード",
    downloadFailed: "ファイルのダウンロードに失敗しました",
    retryProcessing: "処理を再試行",
    delete: "削除",
    deleteTitle: "ドキュメントを削除",
    deleteDescription: "このドキュメントを削除してもよろしいですか？",
    add: "追加",
    files: "ファイル",
    waitForUploads: "現在のアップロードが完了するまでお待ちください",
    filesLimit: "最大{{maxFiles}}件、合計{{maxMb}}MBまで",
    webLinkDescription: "アドレスでページを追加",
    uploadLimit: "最大{{maxFiles}}件のファイルをアップロードできます（合計{{maxMb}}MBまで）",
    webLink: "ウェブリンク",
    addWebLink: "ウェブリンクを追加",
    invalidUrl: "有効なURLを入力してください",
    duplicateLink: "このウェブリンクはすでに追加されています",
    webLinkAdded: "ウェブリンクを追加し、処理を開始しました",
    webLinkFailed: "ウェブリンクを追加できませんでした"
  },
  recordChip: {
    high: "高",
    medium: "中",
    low: "低",
    notNow: "見送り",
    no: "いいえ",
    yes: "はい",
    fail: "不合格",
    failed: "不合格",
    pass: "合格",
    passed: "合格",
    rejected: "却下",
    recommended: "推奨",
    weak: "弱い",
    weakened: "弱まった",
    strong: "強い",
    negative: "ネガティブ",
    positive: "ポジティブ",
    critical: "重大",
    blocked: "ブロック中",
    moderate: "中程度",
    partial: "部分的",
    partially: "部分的",
    conditional: "条件付き",
    watch: "要注意",
    unclear: "不明確",
    atRisk: "リスクあり",
    confirmed: "確認済み",
    onTrack: "順調"
  },
  instructionsDialog: {
    title: "指示",
    sections: "指示のセクション",
    general: "全般",
    presentation: "プレゼンテーション",
    chapters: "チャプター",
    hasInstructions: "指示あり",
    projectTitle: "プロジェクト指示",
    projectDescription: "このプロジェクトのすべてのチャプターで Leo が従う指針です。",
    projectPlaceholder: "例：米国の医療機関に絞り、公開情報のみを引用する。",
    outlineTitle: "アウトライン指示",
    outlineDescription: "プレゼンテーションのアウトラインの作り方です。空欄の場合は標準の動作になります。",
    outlinePlaceholder: "例：10枚のスライドで、結論から始める。",
    chapterTitle: "チャプター指示",
    chapterDescription: "プロジェクト指示に加えて、このチャプターだけに適用される指針です。",
    chapterPlaceholder: "{{name}}への指示を入力",
    thisChapter: "このチャプター",
    close: "閉じる",
    cancel: "キャンセル",
    save: "変更を保存",
    saving: "保存中…"
  },
  instructionsModal: {
    title: "指示",
    general: "全般",
    projectInstructions: "プロジェクト指示",
    presentation: "プレゼンテーション",
    outlineInstructions: "アウトライン指示",
    pages: "ページ",
    pageInstructions: "ページ指示",
    projectPlaceholder: "プロジェクトへの指示を入力",
    outlinePlaceholder: "プレゼンテーションスライドの生成方法をカスタマイズします。空欄の場合は標準の動作になります。",
    pagePlaceholder: "{{name}}への指示を入力",
    thisPage: "このページ",
    saving: "保存中...",
    saveChanges: "変更を保存",
    saveFailed: "指示を保存できませんでした",
    saved: "指示を保存しました"
  },
  presentation: {
    backToOutline: "アウトラインに戻る",
    openInGamma: "Gammaで開く",
    outlineTitle: "プレゼンテーションのアウトライン",
    created: {
      justNow: "たった今作成",
      minutes: "{{count}} 分前に作成",
      hourOne: "{{count}} 時間前に作成",
      hourMany: "{{count}} 時間前に作成",
      dayOne: "{{count}} 日前に作成",
      dayMany: "{{count}} 日前に作成"
    },
    refreshed: {
      justNow: "たった今更新",
      minutes: "{{count}} 分前に更新",
      hourOne: "{{count}} 時間前に更新",
      hourMany: "{{count}} 時間前に更新",
      dayOne: "{{count}} 日前に更新",
      dayMany: "{{count}} 日前に更新"
    },
    updatingSlides: "スライドを更新中...",
    previewSlides: "スライドをプレビュー",
    creating: "作成中...",
    refreshing: "更新中...",
    createSlides: "スライドを作成",
    refreshSlides: "スライドを更新",
    loadingOutline: "プレゼンテーションのアウトラインを読み込み中...",
    refreshingOutline: "アウトラインを更新中",
    creatingOutline: "アウトラインを作成中",
    analyzingLine1: "ドキュメントを分析し、",
    analyzingLine2: "アウトラインを構成しています",
    startTitle: "プレゼンテーションを始めましょう",
    startLine1: "ドキュメントからスライドのアウトラインを作成し、",
    startLine2: "デザインされたプレゼンテーションに変換します",
    createOutline: "アウトラインを作成",
    refreshOutline: "アウトラインを更新",
    slides: "スライド",
    addSlide: "スライドを追加",
    newSlide: "新規スライド",
    deleteSlide: "スライドを削除",
    untitledSlide: "無題のスライド",
    noSlides: "スライドはまだありません。アウトラインを作成して始めましょう",
    slideNumber: "スライド {{number}}",
    slideTitlePlaceholder: "スライドのタイトル",
    keyPointsPlaceholder: "• ポイント1\n• ポイント2\n• ポイント3",
    bold: "太字 (Ctrl+B)",
    italic: "斜体 (Ctrl+I)",
    bulletList: "箇条書き",
    numberedList: "番号付きリスト",
    loadingPresentation: "プレゼンテーションを読み込み中...",
    pdfLoadFailed: "PDFを読み込めませんでした",
    chatPlaceholder: "何でも質問してください...",
    cancel: "キャンセル",
    chatEmptyLine1: "アウトラインを作成すると",
    chatEmptyLine2: "AIとチャットできます",
    outlineReady: "アウトラインの準備ができました！直接編集することも、私に依頼することもできます。",
    steps: {
      indexing: "ドキュメントをインデックス化中",
      planningStructure: "スライド構成を計画中",
      generatingContent: "スライドの内容を生成中",
      readingChapters: "チャプターを読み込み中",
      planningStoryline: "ストーリーラインを計画中",
      writingSlides: "スライドを作成中"
    },
    status: {
      noOutlineTitle: "アウトラインはまだありません",
      noOutlineDescription: "まずアウトラインを生成してください",
      upToDateTitle: "ソースと指示は最新です",
      upToDateDescription: "アウトラインには最新のソースと指示が反映されています。",
      sourceChangedTitle: "ソースが変更されました",
      sourceChangedDescription:
        "ソースドキュメントが変更されました。アウトラインを最新の状態に保つには更新してください。",
      instructionsChangedTitle: "指示が変更されました",
      instructionsChangedDescription:
        "アウトラインの指示が変更されました。アウトラインを最新の状態に保つには更新してください。",
      bothChangedTitle: "ソースと指示が変更されました",
      bothChangedDescription:
        "ソースドキュメントと指示が変更されました。アウトラインを最新の状態に保つには更新してください。"
    },
    notifications: {
      refresh: "更新",
      refreshMessage: "アウトラインを更新してください",
      keepCurrent: "現在のまま",
      keepCurrentMessage: "現在のアウトラインのままにします",
      keptResponse:
        "承知しました。現在のアウトラインのままにします。ツールバーからいつでも更新できますし、私に依頼していただくこともできます。",
      sourceChangedContent: "ソースドキュメントの変更を検出しました。更新内容は次のとおりです:",
      instructionsChangedContent: "アウトラインの指示の変更を検出しました。",
      bothChangedContent: "ソースドキュメントとアウトラインの指示の変更を検出しました。更新内容は次のとおりです:",
      sourceModified: "ソースドキュメントが変更されました",
      instructionsUpdated: "アウトラインの指示が更新されました",
      refreshQuestion: "これらの変更を反映するためにアウトラインを更新しますか？"
    }
  },
  presentationBuilder: {
    pageTitle: "プレゼンテーション",
    title: "プレゼンテーションビルダー",
    views: "ビルダーの表示",
    outline: "アウトライン",
    slides: "スライド",
    emptyTitle: "このプロジェクトからプレゼンテーションを作成",
    emptyDescription: "メモからデザイン済みのスライドまで、3つのステップです。",
    stepLabel: "ステップ {{number}}",
    steps: {
      draftTitle: "アウトラインを作成",
      draftBody: "Leoがプロジェクトをスライドごとのアウトラインにまとめます。",
      editTitle: "スライドを編集",
      editBody: "スライドの並べ替え、書き直し、追加ができます。Leoに変更を頼むこともできます。",
      designTitle: "スライドをデザイン",
      designBody: "アウトラインが整ったら、デザインされたスライドを作成します。"
    },
    outlineInstructions: "アウトライン指示",
    createOutline: "アウトラインを作成",
    draftingOutline: "アウトラインを作成中",
    refreshingOutline: "アウトラインを更新中",
    draftingDescription: "Leoがプロジェクトを読み、スライドごとに構成しています。",
    updatingSlides: "スライドを更新中…",
    createSlides: "スライドを作成",
    refreshSlides: "スライドを更新",
    creatingSlides: "スライドを作成中…",
    refreshingSlides: "スライドを更新中…",
    addSlide: "現在のスライドの後にスライドを追加",
    moveSlide: "スライド {{number}} を移動",
    deleteSlide: "スライド {{number}} を削除",
    slideTitleLabel: "スライド {{number}} のタイトル",
    slideTitlePlaceholder: "スライドのタイトル",
    keyPointsPlaceholder: "このスライドの要点",
    formatting: "書式",
    bold: "太字",
    italic: "斜体",
    bulletList: "箇条書き",
    numberedList: "番号付きリスト"
  }
};

export const createProject: LocaleShape<typeof createProjectEn> = {
  chatTitle: "チャット",
  newProjectTitle: "新規プロジェクト",
  intro:
    "いくつか質問をしながら、ご回答に合わせてプロジェクトを組み立てていきます。すべての項目は任意です — 共有いただくほど、分析はより的確になります",
  languageQuestion:
    "いくつか質問をしながら、ご回答に合わせてプロジェクトを組み立てていきます。すべての項目は任意です — 共有いただくほど、分析はより的確になります\n\nはじめに、希望する言語を選択してください:",
  autoLanguageMsg: "プロジェクトは日本語で進めます。",
  decisionQuestion: "それでは、取り組んでいる決定について教えてください。",
  uploadFiles: "共有したいレポート、データ、または資料はありますか？",
  decisionFromDocuments:
    "資料を拝見すると、次の点について意思決定されようとしているようです：**{{decision}}** この理解で合っていますか？違う場合は、どのように表現されますか？",
  setupCompleteFiles: "ファイル{{fileCount}}件が追加されました。",
  setupCompleteLinks: "リンク{{linkCount}}件が追加されました。",
  setupCompleteFilesAndLinks: "ファイル{{fileCount}}件とリンク{{linkCount}}件が追加されました。",
  setupComplete: "セットアップ完了！取り組んでいる決定について教えてください。",
  projectSetupComplete:
    "プロジェクトセットアップが完了しました！次のステップ「ケースフレーミング」に進み、プロジェクトの作成を完了しましょう。",
  nextStep: "次のステップ",
  navNextCaseFrame: "次へ：ケースフレーム",
  navConfirmed: "確認しました",
  confirmSetup: "確認",
  walkthroughFormat: "これは **{{value}}** 型の意思決定のようです。合っていますか？",
  walkthroughScope: "範囲について、資料から次のように理解しました：{{value}} 合っていますか？",
  walkthroughIntent: "背景にある狙いは次の通りでしょうか：{{value}} 合っていますか？",
  walkthroughSectionFilled: "資料をもとに{{section}}を記入しました。",
  walkthroughWriting: "{{section}}を作成中",
  walkthroughChecking: "{{section}}をこれまでの回答と照合しています",
  walkthroughSectionFilledAdjusted: "資料をもとに{{section}}を記入し、これまでの回答に合わせて調整しました。",
  walkthroughGeneralCheck: "{{section}}の内容で、違うところはありますか？",
  walkthroughLooksRight: "問題ありません",
  walkthroughReady: "分析を開始するのに十分な内容がそろいました。今すぐ開始しますか？それとも残りの質問を続けますか？",
  walkthroughLaunchNow: "今すぐ開始",
  walkthroughKeepGoing: "続ける",
  walkthroughFinishing: "先に、資料をもとに残りのセクションを仕上げます。1分以内に終わります。",
  walkthroughFinishingStatus: "ケースブリーフを仕上げています",
  walkthroughDone:
    "ありがとうございます。資料の内容はこれで反映しました。分析を開始するか、追加したいことがあればお知らせください。",
  walkthroughDoneWithGaps:
    "ありがとうございます。資料から反映できたのはここまでです。{{gaps}}分析を開始するか、追加したいことがあればお知らせください。",
  walkthroughUnreadFiles: "{{files}}は読み込めませんでした。",
  walkthroughUnwrittenSections: "{{sections}}は資料から記入できませんでした。",
  walkthroughReadIncomplete: "資料を最後まで読み込めなかったため、空のままのセクションがあるかもしれません。",
  walkthroughAnythingElse: "さらに詳しく進める前に、追加や修正はありますか？",
  navBackProjectSetup: "戻る：プロジェクトセットアップ",
  backAtProjectSetupPrefix: "プロジェクトセットアップに戻りました。",
  backAtProjectSetupAllSet:
    "プロジェクトセットアップに戻りました。ここはすべて設定済みです。変更したい点があればお知らせください。",
  backAtProjectSetupNoChanges: "承知しました、変更なしですね。ケースフレーミングに進みましょう。",
  navCaseFrameResume:
    "ケースフレームに移りました。プロジェクトについて何でも聞いてください。左側でブリーフを編集することもできます。",
  filesGotIt: "了解しました！ファイルやリンクはいつでもここに追加できます。",
  vaultSuccess: "ファイルがVaultに正常に追加されました。",
  vaultLinkSuccess: "リンクがVaultに正常に追加されました。",
  refineIntro: "こんにちは！プロジェクトフレームの調整をお手伝いします。何を変更しますか？",
  versionDraftReady: "ドラフトの準備ができました。必要な変更を加えて、準備ができたら分析を開始してください。",
  essentialsCovered: "基本は網羅できています。これで始めるには十分です。さらに追加すれば、より深い洞察が得られます。",
  skipForNow: "今はスキップ",
  upload: "アップロード",
  webLink: "ウェブリンク",
  skip: "スキップ",
  save: "保存",
  confirmDecision: "はい、その通りです",
  otherDecision: "その他",
  confirmDecisionReply: "はい、その通りです。",
  launchAnalysis: "分析を開始",
  keepEditing: "編集を続ける",
  keepEditingPrompt: "ケースブリーフをさらに磨き上げたいです。ブリーフを最も強化できる次の質問をしてください。",
  failedUpload: "アップロード失敗: {{name}}",
  draftUploadLimit: "1つのプロジェクトの資料は合計100MBまでです。アップロードされなかったファイル: {{names}}",
  dimensionDropdown: "このプロジェクトに最も適した{{name}}は何ですか？",
  dimensionText: "このプロジェクトの{{name}}は何ですか？",
  dimensionConfirm: "確認",
  dimensionSkip: "スキップ",
  send: "送信",
  composerPlaceholder: "AIにメッセージを入力...",
  composerParkedPlaceholder: "上のボタンから操作を続けてください...",
  titles: {
    projectSetup: "プロジェクトセットアップ",
    caseFrame: "ケースフレーム",
    newVersion: "新しいバージョン",
    reviewRelaunch: "確認して再実行"
  },
  header: {
    percentComplete: "{{value}}% 完了",
    readyToLaunch: "開始できます",
    launchNudge:
      "必要な項目はそろいました。準備ができたら分析を開始してください。さらに書き込むと分析の精度が上がります。",
    launchNudgeDismiss: "OK",
    back: "戻る",
    launching: "開始しています...",
    resetProject: "プロジェクトをリセット",
    analysisSettings: "分析設定",
    setupIncompleteTitle: "セットアップが未完了です",
    setupIncompleteDescription: "続けるにはプロジェクト名を入力してください。開始に必要な最小限の情報です",
    setupAnswerInChatDescription: "続けるにはチャットの質問に答えてください",
    frameEmptyTitle: "プロジェクトフレームが未入力です",
    frameEmptyDescription: "プロジェクト名と言語を入力してください。開始に必要な最小限の情報です。",
    frameIncompleteTitle: "プロジェクトフレームが不完全です",
    frameIncompleteDescription: "チャットで共有する情報が多いほど、分析はより正確になります。",
    frameCompleteTitle: "プロジェクトフレームが完成しました",
    frameCompleteDescription: "徹底的で正確な分析を実行するために必要な情報がすべて揃っています。",
    resetTitle: "プロジェクトをリセット",
    resetDescription:
      "リセットすると、プロジェクトの情報はすべて消去されます。もう一度すべて入力する必要があります。よろしいですか？",
    resetConfirm: "リセット",
    launchIncompleteTitle: "ブリーフが不完全です",
    launchIncompleteDescription:
      "不完全なブリーフで分析を開始すると、結果の精度が下がったり、重要なインサイトを見落とす可能性があります。",
    launchCompleteTitle: "最後にひとつ",
    launchCompleteDescription: "開始する前にブリーフをご確認ください。結果の品質を左右します。"
  },
  status: {
    analyzingMessage: "メッセージを分析中...",
    readingDocuments: "ドキュメントを読み込み中...",
    searchingVault: "アップロードされたドキュメントを検索中...",
    searchingWeb: "ウェブを検索中...",
    updatingBrief: "ケースフレームを更新中...",
    craftingResponse: "回答を作成中..."
  },
  documentStatus: {
    reading: "読み込み中…",
    read: "ケースブリーフに追加しました",
    unreadable: "このファイルを読み込めませんでした"
  },
  launch: {
    launched: "分析を開始しました",
    failed: "分析の開始に失敗しました。もう一度お試しください。"
  },
  instructionsSaveFailed: "指示の保存に失敗しました。ローカルには保持されていますが、サーバーには保存されていません。",
  chatError: "回答の取得に失敗しました。もう一度お試しください。",
  chatErrorMessage: "申し訳ありません。問題が発生しました。もう一度お試しください。",
  newProject: {
    loading: "読み込み中…",
    title: "新規プロジェクト",
    untitled: "無題のプロジェクト",
    vault: "保管庫",
    cancel: "キャンセル",
    close: "閉じる",
    launch: "分析を開始",
    launching: "開始しています…",
    launchBlockedDecision: "分析を開始するには、先に意思決定を確認してください。",
    launchBlockedName: "分析を開始するには、先にプロジェクト名を付けてください。",
    launchReadyTitle: "分析を開始しますか？",
    launchReadyDescription:
      "ケースフレームはすべての結果の土台になるため、開始前に一度ご確認ください。分析には約3時間かかります。",
    launchEarlyTitle: "今すぐ分析を開始しますか？",
    launchEarlyDescription:
      "ケースフレームがまだ完成していないため、結果の精度が下がる可能性があります。分析には約3時間かかります。",
    reviewFrame: "フレームを確認",
    keepGoing: "続ける",
    launchAnyway: "このまま開始",
    caseFrame: "ケースフレーム",
    completeness: "完成度",
    completenessValue: "{{percent}}% 完了",
    sectionActive: "Leo がこのセクションについて質問しています",
    sectionSettled: "確定済み",
    sectionPlaceholder: "このセクションを記入…",
    analysisSettings: "分析設定",
    generationModel: "生成モデル",
    skipAnalysis: "分析の生成をスキップ",
    skipAnalysisNote: "分析をスキップしても、ドキュメントの解析は行われます。",
    saveChanges: "変更を保存",
    linkTitle: "ウェブリンクを追加",
    linkLabel: "リンク",
    linkPlaceholder: "https://",
    linkInvalid: "リンクの形式ではないようです。",
    addLink: "リンクを追加",
    addFiles: "ファイルを追加",
    addWebLink: "ウェブリンクを追加",
    sourceRead: "読み込み済み",
    sourceReading: "読み込み中",
    remove: "削除",
    removeSource: "{{name}} を削除",
    dropFiles: "ここにファイルをドロップ",
    dropHint: "PDF、Word、PowerPoint、Excel、またはウェブリンク。",
    projectName: "プロジェクト名",
    description: "説明",
    descriptionPlaceholder: "短い説明を追加",
    uploadPicture: "画像をアップロード",
    removePicture: "画像を削除",
    language: "言語",
    version: "バージョン",
    visibility: "公開範囲",
    organization: "組織",
    private: "非公開",
    showDetails: "プロジェクトの詳細を表示",
    languagePlaceholder: "選択…",
    notSet: "未設定",
    shufflePicture: "画像を入れ替え",
    attachFile: "ファイルを添付",
    tags: {
      addTag: "タグを追加",
      tagWithValue: "{{name}}: {{value}}",
      removeTagFromProject: "{{tag}} をプロジェクトから削除",
      removeFromProject: "プロジェクトから削除",
      allTagsOnProject: "組織のタグはすべてこのプロジェクトに付いています。",
      noTagsYet: "組織にはまだタグがありません。",
      manageTags: "タグを管理",
      backToTags: "タグに戻る",
      edit: "編集",
      optionsOf: "{{name}} の選択肢",
      current: "現在の値",
      noOptions: "選択肢はまだありません。",
      noOptionsAddWithEdit: "選択肢はまだありません。「編集」から追加してください。",
      setTagValue: "{{name}} を設定",
      typeTagValue: "{{name}} を入力",
      pressEnter: "Enter で確定",
      manageTagsTitle: "タグを管理",
      tagChangesDescription: "変更は組織内のすべてのプロジェクトに反映されます。",
      createNewTag: "新しいタグを作成",
      done: "完了",
      deleteTagForEveryone: "{{name}} を全員から削除",
      deleteForEveryone: "全員から削除",
      deleteTagTitle: "「{{name}}」を全員から削除しますか？",
      deleteTagDescription: "このタグが付いたすべてのプロジェクトから削除され、今後は候補に表示されません。",
      deleteTagDescriptionHere:
        "このプロジェクトを含め、このタグが付いたすべてのプロジェクトから削除され、今後は候補に表示されません。",
      editTagTitle: "「{{name}}」を編集",
      newTagTitle: "新しいタグ",
      newTagDescription: "組織内のすべてのプロジェクトで使えます。",
      tagName: "名前",
      tagNamePlaceholder: "例：ステージ、地域、案件担当",
      tagNameTaken: "組織にはすでに「{{name}}」タグがあります。",
      tagType: "種類",
      tagTypeGroup: "タグの種類",
      tagTypeOptions: "選択肢",
      tagTypeText: "テキスト",
      tagTypeOptionsHint: "各プロジェクトがこの中から1つを選びます。",
      tagTypeTextHint: "各プロジェクトが値を入力します。",
      options: "選択肢",
      removeOption: "{{option}} を削除",
      addOption: "選択肢を追加",
      newOption: "新しい選択肢",
      addAnotherOption: "選択肢をさらに追加",
      typeAnOption: "選択肢を入力",
      removedOptionWarning: "削除された選択肢を選んでいたプロジェクトからは、このタグが外れます。",
      anyText: "任意のテキスト",
      noOptionsSummary: "選択肢はまだありません",
      delete: "削除",
      createTag: "タグを作成",
      saveFailed: "タグを保存できませんでした",
      deleteFailed: "タグを削除できませんでした"
    }
  },
  leo: {
    welcomeTitle: "一緒にプロジェクトを作成しましょう",
    welcomeDescription: "ここでセクションごとに順番にご案内します。",
    welcomePrompt: "まずはファイルかリンクから始めましょう。",
    addFiles: "ファイルを追加",
    addWebLink: "ウェブリンクを追加",
    remove: "{{name}} を削除",
    uploading: "アップロード中…",
    thinking: "考え中",
    conversation: "Leo との会話",
    panel: "Leo、Innovera インテリジェンス",
    hide: "Leo を隠す",
    close: "Leo を閉じる",
    ask: "Leo に質問",
    askWaiting: "Leo に質問、返答待ち",
    waiting: "Leo が返答を待っています",
    dismiss: "閉じる",
    closeOverlay: "閉じる",
    message: "Leo にメッセージ",
    attach: "添付",
    files: "ファイル",
    webLink: "ウェブリンク",
    send: "送信",
    disclaimer: "Leo は間違えることがあります。重要な内容はご確認ください。",
    more: "他 {{count}} 件",
    done: "完了",
    scrollToEnd: "最後までスクロール",
    replyPlaceholder: "Leo に返信",
    typePlaceholder: "回答を入力…",
    orTypePlaceholder: "または回答を入力…",
    changePlaceholder: "変更内容を入力…",
    askPlaceholder: "Leo に何でも質問…",
    changeIt: "いいえ、変更します",
    changeItReply: "いいえ、変更します。",
    changePrompt: "どのように変更しますか？",
    setupComplete: "セットアップが完了しました。確認すると、ケースフレームを一緒に見ていきます。"
  },
  editor: {
    placeholder: "プロジェクトフレームを入力してください...",
    projectName: "プロジェクト名",
    projectDescription: "プロジェクトの説明",
    undo: "元に戻す",
    redo: "やり直す",
    bold: "太字",
    italic: "斜体",
    underline: "下線"
  }
};

export const settings: LocaleShape<typeof settingsEn> = {
  title: "設定",
  groups: {
    account: "アカウント",
    organization: "組織"
  },
  tabs: {
    profile: "プロフィール",
    preferences: "環境設定",
    general: "一般",
    team: "チーム"
  },
  common: {
    save: "変更を保存",
    saving: "保存しています...",
    cancel: "キャンセル",
    show: "表示",
    hide: "非表示"
  },
  profile: {
    heading: "プロフィール",
    uploadPhoto: "写真をアップロード",
    removePhoto: "写真を削除",
    removing: "削除しています...",
    photoHintSize: "正方形の画像（400×400ピクセル）を推奨します",
    photoHintFormat: "JPG、PNG、WebP形式、5MBまで",
    firstName: "名",
    lastName: "姓",
    invalidFileType: "ファイル形式が正しくありません。JPG、PNG、WebPのみ使用できます。",
    fileTooLarge: "ファイルサイズは5MB以下にしてください。",
    photoRemoved: "プロフィール写真を削除しました",
    photoRemoveFailed: "写真を削除できませんでした。もう一度お試しください。",
    updated: "プロフィールを更新しました",
    updateFailed: "プロフィールを更新できませんでした。もう一度お試しください。"
  },
  password: {
    heading: "パスワードの変更",
    current: "現在のパスワード",
    new: "新しいパスワード",
    confirm: "新しいパスワード（確認）",
    minLength: "8文字以上",
    hasNumber: "数字を1文字以上",
    hasCapital: "大文字を1文字以上",
    matches: "パスワードが一致しています",
    changed: "パスワードを変更しました。",
    changeFailed: "パスワードを変更できませんでした。"
  },
  preferences: {
    heading: "環境設定",
    uiLanguage: "表示言語",
    uiLanguageHint: "ボタン、メニュー、ラベルの言語です。プロジェクトの内容はプロジェクトの言語に従います。",
    saved: "環境設定を保存しました",
    saveFailed: "環境設定を保存できませんでした"
  },
  languageName: {
    english: "英語",
    japanese: "日本語"
  },
  snippets: {
    heading: "カスタムスニペットURL",
    description:
      "AIプロンプトを取得するカスタムURLを設定します。設定すると、既定のスニペットサービスの代わりにこのURLが使用されます。",
    none: "カスタムスニペットURLは設定されていません。既定のサービスを使用しています。",
    edit: "編集",
    clear: "クリア",
    configure: "URLを設定",
    urlLabel: "スニペットURL",
    invalidUrl: "http:// または https:// で始まる有効なURLを入力してください",
    save: "保存",
    updated: "カスタムスニペットURLを更新しました",
    updateFailed: "カスタムスニペットURLを更新できませんでした"
  },
  roles: {
    clientUser: "メンバー",
    clientAdmin: "管理者",
    systemAdmin: "システム管理者"
  },
  accessModes: {
    edit: "編集",
    read: "閲覧"
  },
  statuses: {
    active: "有効",
    pending: "保留中",
    inactive: "無効",
    expired: "期限切れ"
  },
  team: {
    you: "（あなた）",
    projectCount: "{{count}}件のプロジェクト",
    selectRow: "{{name}}の行を選択",
    resendInvite: "招待を再送",
    resending: "再送しています...",
    cancelInvite: "招待を取り消す",
    cancelling: "取り消しています...",
    manageAccess: "アクセス権を管理",
    inviteCancelled: "招待を取り消しました",
    inviteCancelFailed: "招待を取り消せませんでした",
    inviteResent: "招待を再送しました",
    inviteResendFailed: "招待を再送できませんでした"
  },
  invite: {
    title: "この組織にメンバーを招待",
    memberRole: "メンバーの役割",
    selectOption: "選択してください",
    accessMode: "アクセス権限",
    selectAccessMode: "アクセス権限を選択",
    firstName: "名",
    firstNamePlaceholder: "名を入力",
    lastName: "姓",
    lastNamePlaceholder: "姓を入力",
    email: "メールアドレス",
    emailPlaceholder: "メールアドレスを入力",
    send: "招待を送信",
    sent: "招待を送信しました",
    sendFailed: "招待を送信できませんでした",
    invalidForm: "入力内容に誤りがあります。修正してもう一度お試しください。",
    roleRequired: "役割は必須です",
    firstNameRequired: "名は必須です",
    firstNameTooShort: "名は2文字以上で入力してください",
    lastNameRequired: "姓は必須です",
    lastNameTooShort: "姓は2文字以上で入力してください",
    emailRequired: "メールアドレスは必須です",
    emailInvalid: "メールアドレスの形式が正しくありません"
  },
  manageAccess: {
    title: "{{name}}のアクセス権を管理",
    memberRole: "メンバーの役割",
    selectRole: "役割を選択",
    accessMode: "アクセス権限",
    selectAccessMode: "アクセス権限を選択",
    memberStatus: "メンバーの状態",
    selectStatus: "状態を選択",
    saveRole: "役割を保存",
    noChanges: "変更はありません。",
    updated: "ユーザーを更新しました",
    updateFailed: "ユーザーを更新できませんでした。もう一度お試しください。"
  },
  teamTable: {
    heading: "チーム",
    searchPlaceholder: "メンバーを検索",
    remove: "削除",
    user: "ユーザー",
    role: "役割",
    projects: "プロジェクト",
    accountStatus: "アカウントの状態",
    dateAdded: "追加日",
    actions: "操作",
    selectAll: "すべての行を選択",
    previous: "前へ",
    next: "次へ",
    usersSelected_one: "{{count}}人のユーザーを選択中",
    usersSelected_other: "{{count}}人のユーザーを選択中",
    showingResults: "{{total}}件中{{shown}}件を表示",
    fetchFailed: "組織データを取得できませんでした"
  },
  general: {
    heading: "一般",
    loading: "組織データを読み込んでいます...",
    aiModel: "AIモデル",
    uploadLogo: "ロゴをアップロード",
    removeLogo: "ロゴを削除",
    removing: "削除しています...",
    logoHintSize: "正方形の画像（400×400ピクセル）を推奨します",
    logoHintFormat: "JPG、PNG、WebP形式、5MBまで",
    orgName: "組織名",
    orgInstructions: "組織の指示",
    orgInstructionsPlaceholder: "指示を入力してください",
    invalidFileType: "ファイル形式が正しくありません。JPG、PNG、WebPのみ使用できます。",
    fileTooLarge: "ファイルサイズは5MB以下にしてください。",
    orgIdMissing: "組織IDが見つかりません",
    logoRemoved: "組織のロゴを削除しました",
    logoRemoveFailed: "ロゴを削除できませんでした。もう一度お試しください。",
    updated: "組織情報を更新しました",
    updateFailed: "組織情報を更新できませんでした。もう一度お試しください。"
  }
};

export const projects: LocaleShape<typeof projectsEn> = {
  toolbar: {
    searchProjects: "プロジェクトを検索",
    addProject: "プロジェクトを追加",
    clearSearch: "検索をクリア"
  },
  sidebar: {
    projects: "プロジェクト"
  },
  card: {
    open: "開く",
    duplicate: "複製",
    delete: "削除",
    version: "バージョン",
    status: "ステータス",
    lastModified: "最終更新",
    modifiedBy: "更新者",
    createdBy: "作成者:",
    photoCredit: "写真:",
    photoSource: "ウィキメディア・コモンズ",
    private: "非公開",
    projectName: "プロジェクト名",
    creator: "作成者",
    actions: "{{name}} の操作",
    createVersion: "バージョンを作成",
    versionNamePlaceholder: "新しいバージョン..."
  },
  sort: {
    default: "並び替え",
    sortByHeading: "並び替え基準",
    orderHeading: "順序",
    orders: {
      azAsc: "A–Z",
      azDesc: "Z–A",
      newestFirst: "新しい順",
      oldestFirst: "古い順",
      completedFirst: "完了が先",
      draftFirst: "下書きが先"
    }
  },
  filters: {
    heading: "フィルター",
    more: "その他",
    clearAll: "すべてクリア",
    notSet: "未設定",
    noValues: "値がありません",
    searchIn: "{{name}}を検索",
    noMatches: "一致する項目がありません"
  },
  dimensions: {
    heading: "ディメンション",
    newTitle: "新規ディメンション",
    editTitle: "ディメンションを編集",
    nameLabel: "名前",
    namePlaceholder: "ディメンション名...",
    typeLabel: "タイプ",
    optionsLabel: "選択肢",
    optionPlaceholder: "選択肢を追加...",
    addOption: "追加",
    removeOption: "{{option}} を削除",
    create: "作成",
    save: "保存",
    editAction: "編集",
    deleteAction: "削除",
    dragToReorder: "ドラッグして並べ替え",
    confirmDelete: "{{name}} を削除しますか？",
    confirmDeleteBody: "このフィールドをすべてのプロジェクトから完全に削除します。",
    confirmDeleteAction: "削除を確定",
    cancelDeleteAction: "削除をキャンセル",
    emptyTitle: "ディメンションはまだありません",
    emptyBody: "プロジェクトで重視する項目を追跡するために、ディメンションを追加しましょう。",
    subtitleText: "テキスト",
    subtitleDropdown_one: "ドロップダウン · 選択肢 {{count}} 件",
    subtitleDropdown_other: "ドロップダウン · 選択肢 {{count}} 件",
    types: {
      dropdown: "ドロップダウン",
      dropdownDescription: "ユーザーがリストから選択します",
      text: "テキスト",
      textDescription: "ユーザーが自由に入力します"
    },
    confirmChangeTitle: "変更の確認",
    confirmChangeAction: "確定",
    affectedCount_one: "(プロジェクトの値 {{count}} 件がクリアされます。)",
    affectedCount_other: "(プロジェクトの値 {{count}} 件がクリアされます。)",
    toasts: {
      created: "ディメンションを作成しました",
      updated: "ディメンションを更新しました",
      removed: "ディメンションを削除しました"
    },
    errors: {
      createFailed: "作成できませんでした",
      updateFailed: "更新できませんでした",
      deleteFailed: "削除できませんでした",
      reorderFailed: "並べ替えできませんでした"
    }
  },
  properties: {
    heading: "プロパティ",
    showAll: "すべて表示",
    hideAll: "すべて非表示",
    system: "システム",
    alwaysVisible: "常に表示",
    dimensions: "ディメンション",
    noDimensions: "ディメンションがありません",
    manageDimensions: "ディメンションを管理"
  },
  chat: {
    placeholder: "プロジェクトについて質問してください…",
    welcome: "こんにちは！プロジェクトについて何でも聞いてください — ステータス、担当者、最近の変更など。",
    closeAriaLabel: "チャットを閉じる",
    optionsMenuLabel: "チャットのオプション",
    stop: "停止",
    noResponseFallback: "申し訳ありません、回答を生成できませんでした。",
    genericErrorFallback: "申し訳ありません、問題が発生しました。もう一度お試しください。"
  },
  savedFilters: {
    heading: "保存したフィルター",
    save: "保存",
    saveTitle: "フィルターを保存",
    renameTitle: "フィルターの名前を変更",
    nameLabel: "名前",
    namePlaceholder: "フィルター名...",
    emptyTitle: "保存したフィルターはまだありません",
    emptyBody: "フィルターを設定して保存すると、ワンクリックで再利用できます。",
    rename: "名前を変更",
    delete: "削除",
    more: "その他",
    created: "フィルターを保存しました",
    renamed: "フィルターの名前を変更しました",
    deleted: "「{{name}}」を削除しました",
    createFailed: "フィルターを保存できませんでした",
    renameFailed: "フィルターの名前を変更できませんでした",
    deleteFailed: "フィルターを削除できませんでした"
  },
  status: {
    options: {
      framing: "フレーミング",
      generating: "生成中",
      complete: "完了",
      failed: "失敗"
    },
    framingProgress: "フレーミング ({{percent}}%)",
    generatingProgress: "生成中 ({{generated}}/{{total}})",
    completeProgress: "完了 ({{generated}}/{{total}})"
  },
  errors: {
    loadFailedTitle: "プロジェクトを読み込めませんでした",
    loadFailedBody: "サーバー側で問題が発生しました。しばらくしてからもう一度お試しください。",
    retry: "再試行",
    duplicateFailed: "プロジェクトを複製できませんでした",
    deleteDraftFailed: "下書きを削除できませんでした",
    deleteProjectFailed: "プロジェクトを削除できませんでした"
  },
  toasts: {
    projectDuplicated: "プロジェクトを複製しました",
    draftDeleted: "下書きを削除しました",
    projectDeleted: "プロジェクトを削除しました"
  },
  deleteModal: {
    draftTitle: "下書きを削除",
    projectTitle: "プロジェクトを削除",
    draftDescription: "この操作は取り消せません。下書きとそのすべてのデータが完全に削除されます。",
    projectDescription:
      "この操作は取り消せません。プロジェクトのすべてのデータ、バージョン、設定が完全に削除されます。",
    deleteAction: "削除"
  }
};
